<template>
  <div class="album-manage-container">
    <!-- 1. 列表页顶部操作栏 -->
    <div class="header-action-bar">
      <el-button type="primary" icon="el-icon-plus" @click="openCreateDialog">创建相册</el-button>
    </div>

    <!-- 2. 相册表格列表 -->
    <el-table
      v-loading="loading"
      :data="dataList"
      style="width: 100%"
      border
      stripe
    >
      <el-table-column type="index" label="序号" width="60" align="center" />

      <el-table-column label="相册封面" width="110" align="center">
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.coverUrl"
            lazy
            fit="cover"
            class="cover-img"
            :src="scope.row.coverUrl"
            :preview-src-list="[scope.row.coverUrl]"
          />
          <div v-else class="no-cover">
            <i class="el-icon-picture-outline" />
            <span>暂无封面</span>
          </div>
        </template>
      </el-table-column>

      <el-table-column prop="albumName" label="相册名字" min-width="180" show-overflow-tooltip>
        <template slot-scope="scope">
          <router-link class="album-link" target="_blank" :to="`/image/${scope.row.albumId}`">
            {{ scope.row.albumName }}
          </router-link>
        </template>
      </el-table-column>

      <el-table-column prop="total" label="照片数量" width="100" align="center">
        <template slot-scope="scope">
          <el-tag type="info" size="small">{{ scope.row.total || 0 }} 张</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="scope" label="可见范围" width="120" align="center">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.scope === 1" size="small" type="info">本人可见</el-tag>
          <el-tag v-else-if="scope.row.scope === 2" size="small" type="success">所有人可见</el-tag>
          <el-tag v-else-if="scope.row.scope === 3" size="small" type="warning">VIP 可见</el-tag>
        </template>
      </el-table-column>

      <el-table-column prop="createAt" label="发布时间" width="170" align="center" />

      <el-table-column label="操作" width="160" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button
            size="mini"
            type="primary"
            plain
            icon="el-icon-picture"
            @click="handleManagePhotos(scope.row)"
          >管理照片</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 3. 主页面分页条 -->
    <div class="pagination-wrapper">
      <el-pagination
        :small="screenWidth <= 768"
        background
        layout="total, prev, pager, next, jumper"
        :page-size="pageSize"
        :current-page="currentPage"
        :total="totalSize"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 4. 对话框：创建相册 -->
    <el-dialog
      title="创建相册"
      :visible.sync="createAlbumDialog"
      width="450px"
      append-to-body
      @close="resetCreateForm"
    >
      <el-form ref="formRef" :model="form" :rules="rules" label-width="90px">
        <el-form-item label="相册名称" prop="albumName">
          <el-input v-model="form.albumName" placeholder="请输入相册名称（不超过50字）" maxlength="50" show-word-limit />
        </el-form-item>
        <el-form-item label="可见范围" prop="scope">
          <el-select v-model="form.scope" placeholder="选择可见范围" style="width: 100%">
            <el-option label="本人可见" :value="1" />
            <el-option label="所有人可见" :value="2" />
            <el-option label="VIP 可见" :value="3" />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="createAlbumDialog = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="onCreateAlbum">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 5. 相册照片管理弹窗 -->
    <el-dialog
      :title="`相册照片管理 - 【${currentAlbum ? currentAlbum.albumName : ''}】`"
      :visible.sync="manageDialogVisible"
      width="70%"
      append-to-body
      @close="handleManageDialogClose"
    >
      <!-- 顶部操作与统计工具栏 -->
      <div class="dialog-toolbar">
        <div class="photo-count-info">
          共计 <strong>{{ photoTotalSize }}</strong> 张照片
        </div>
        <el-button
          :type="showUploadPanel ? 'warning' : 'primary'"
          size="small"
          :icon="showUploadPanel ? 'el-icon-arrow-up' : 'el-icon-upload2'"
          @click="showUploadPanel = !showUploadPanel"
        >
          {{ showUploadPanel ? '收起上传面板' : '上传新照片' }}
        </el-button>
      </div>

      <!-- 可折叠上传面板 -->
      <el-collapse-transition>
        <div v-show="showUploadPanel" class="upload-panel">
          <el-upload
            :action="imgOssUrl"
            :headers="imgHeaders"
            :data="imgData"
            :file-list="uploadImages"
            :multiple="true"
            :limit="40"
            :with-credentials="false"
            list-type="picture-card"
            :before-upload="handleBeforeUpload"
            :on-success="handleOnSuccess"
            :on-error="handleOnError"
            :on-remove="handleOnRemove"
            :on-preview="handleOnPreview"
          >
            <i class="el-icon-plus" />
          </el-upload>
        </div>
      </el-collapse-transition>

      <!-- 照片列表展示画廊 -->
      <div v-loading="photosLoading" class="photo-grid">
        <div v-for="(photo, index) in photoList" :key="photo.imageFileId || index" class="photo-item">
          <!-- 动态绑定 ref 用于触发原生大图预览 -->
          <el-image
            :ref="`photoImg_${index}`"
            class="photo-img"
            :src="photo.thumbnailUrl"
            fit="cover"
            :preview-src-list="previewList"
          />
          <div class="photo-mask">
            <!-- 1. 查看原图按钮 -->
            <el-tooltip content="查看原图" placement="top" :open-delay="200">
              <i class="el-icon-zoom-in photo-action-btn" @click="handlePreviewPhoto(photo)" />
            </el-tooltip>

            <!-- 2. 设置为封面按钮 -->
            <el-tooltip content="设为封面" placement="top" :open-delay="200">
              <i class="el-icon-picture-outline-round photo-action-btn" @click="onSetCover(photo)" />
            </el-tooltip>

            <!-- 3. 删除照片按钮 -->
            <el-tooltip content="删除照片" placement="top" :open-delay="200">
              <i class="el-icon-delete photo-action-btn delete-btn" @click="handleDeletePhoto(photo, index)" />
            </el-tooltip>
          </div>
        </div>

        <!-- 无照片空状态 -->
        <div v-if="!photosLoading && photoList.length === 0" class="empty-photos">
          <el-empty description="该相册暂无照片，点击右上角上传照片吧~" />
        </div>
      </div>

      <!-- 弹窗底部：加了分页栏 -->
      <div slot="footer" class="dialog-footer photo-dialog-footer">
        <el-pagination
          small
          background
          layout="total, sizes, prev, pager, next"
          :page-sizes="[12, 24, 36, 48]"
          :page-size="photoPageSize"
          :current-page="photoCurrentPage"
          :total="photoTotalSize"
          @size-change="handlePhotoSizeChange"
          @current-change="handlePhotoCurrentChange"
        />
        <el-button size="small" @click="manageDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>

    <!-- 照片大图预览 Dialog -->
    <el-dialog :visible.sync="previewDialogVisible" append-to-body>
      <img width="100%" :src="dialogImageUrl" alt="预览图">
    </el-dialog>
  </div>
</template>

<script>
import { createAlbum, getAlbumItems, doAlbumAction, getUserAlbumList, setAlbumCover } from '@/api/vod'
import { getPhotoChannelInfo } from '@/api/file'

// 存储文件与上传后台 ID 的映射关系
const imageFileMap = new Map()

export default {
  name: 'ImagePost',
  data() {
    return {
      screenWidth: document.body.clientWidth,
      currentUser: null,
      loading: false,
      currentPage: 1,
      pageSize: 10,
      totalSize: 0,
      dataList: [],

      // 新建相册表单
      createAlbumDialog: false,
      submitting: false,
      form: {
        albumName: '',
        scope: 1,
        postType: 1
      },
      rules: {
        albumName: [{ required: true, message: '请输入相册名称', trigger: 'blur' }],
        scope: [{ required: true, message: '请选择可见范围', trigger: 'change' }]
      },

      // 管理相册弹窗
      manageDialogVisible: false,
      photosLoading: false,
      showUploadPanel: false,
      currentAlbum: null,
      photoList: [],

      // 弹窗照片列表分页参数
      photoCurrentPage: 1,
      photoPageSize: 12,
      photoTotalSize: 0,

      // 动态 OSS 上传相关变量
      imgOssUrl: '',
      imgHeaders: {
        Authorization: ''
      },
      imgData: {
        channelId: 105
      },
      uploadImages: [],
      dialogImageUrl: '',
      previewDialogVisible: false
    }
  },
  computed: {
    // 提取所有照片的原图/缩略图作为预览列表
    previewList() {
      return this.photoList.map(p => p.imageUrl || p.thumbnailUrl)
    }
  },
  created() {
    this.currentUser = this.$store.state.userInfo
    document.title = '我的图片稿件'
    this.getData()
  },
  mounted() {
    window.onresize = () => {
      this.screenWidth = document.body.clientWidth
    }
  },
  methods: {
    // 异步获取通道及 OSS 上传地址信息
    fetchPhotoChannelConfig() {
      getPhotoChannelInfo().then(res => {
        if (res.code === 0) {
          const resData = res.data
          this.imgOssUrl = resData.ossUrl
          this.imgHeaders.Authorization = 'Bearer ' + resData.token
        } else {
          this.$notify({
            title: '提示',
            message: '获取 OSS 服务器地址失败, 暂时无法上传图片',
            type: 'error',
            duration: 3000
          })
        }
      }).catch(() => {
        this.$notify({
          title: '提示',
          message: '获取 OSS 服务器地址失败, 暂时无法上传图片',
          type: 'warning',
          duration: 3000
        })
      })
    },

    // 获取相册数据列表
    getData() {
      this.loading = true
      const queryInfo = {
        userId: this.currentUser.userIdStr,
        postType: 1,
        pn: this.currentPage
      }
      getUserAlbumList(queryInfo)
        .then(resp => {
          if (resp.code === 0) {
            this.dataList = resp.data.list || []
            this.totalSize = resp.data.totalSize || 0
          } else {
            this.$message.error(resp.msg || '获取相册列表失败')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleCurrentChange(pageNumber) {
      this.currentPage = pageNumber
      this.getData()
      window.scrollTo(0, 0)
    },

    // --- 创建相册相关逻辑 ---
    openCreateDialog() {
      this.createAlbumDialog = true
    },
    resetCreateForm() {
      if (this.$refs.formRef) {
        this.$refs.formRef.resetFields()
      }
      this.form = { albumName: '', scope: 1, postType: 1 }
    },
    onCreateAlbum() {
      this.$refs.formRef.validate(valid => {
        if (!valid) return
        this.submitting = true
        createAlbum(this.form)
          .then(res => {
            if (res.code === 0) {
              this.$message.success('相册创建成功')
              this.createAlbumDialog = false
              this.currentPage = 1
              this.getData()
            } else {
              this.$message.warning(res.msg || '创建失败')
            }
          })
          .catch(error => {
            this.$message.error(error.message || '网络异常')
          })
          .finally(() => {
            this.submitting = false
          })
      })
    },

    // --- 管理相册及照片列表 ---
    handleManagePhotos(row) {
      this.currentAlbum = row
      this.manageDialogVisible = true
      this.showUploadPanel = false
      this.photoCurrentPage = 1 // 打开弹窗重置为第1页
      this.fetchPhotoChannelConfig()
      this.fetchPhotos(row.albumId)
    },
    fetchPhotos(albumId) {
      this.photosLoading = true

      const queryInfo = {
        albumId: albumId,
        pn: this.photoCurrentPage,
        ps: this.photoPageSize
      }
      getAlbumItems(queryInfo)
        .then(res => {
          if (res.code === 0 && res.data) {
            const imageListObj = res.data.imageList || {}
            this.photoList = imageListObj.list || []
            this.photoTotalSize = imageListObj.totalSize || 0
            this.photoPageSize = imageListObj.pageSize || 12
          } else {
            this.$message.error(res.msg || '获取照片失败')
          }
        })
        .finally(() => {
          this.photosLoading = false
        })
    },
    // 相册弹窗分页改变
    handlePhotoCurrentChange(page) {
      this.photoCurrentPage = page
      if (this.currentAlbum) {
        this.fetchPhotos(this.currentAlbum.albumId)
      }
    },
    // 相册弹窗每页条数改变
    handlePhotoSizeChange(size) {
      this.photoPageSize = size
      this.photoCurrentPage = 1
      if (this.currentAlbum) {
        this.fetchPhotos(this.currentAlbum.albumId)
      }
    },

    // 手动触发图片预览大图
    handlePreviewPhoto(photo) {
      this.dialogImageUrl = photo.originalUrl || photo.imageUrl || photo.thumbnailUrl
      this.previewDialogVisible = true
    },
    onSetCover(item) {
      const jsonData = {
        albumId: this.currentAlbum.albumId,
        postId: item.imageFileId || item.id,
        action: 3
      }
      setAlbumCover(jsonData).then(res => {
        if (res.code === 0) {
          this.$message.success('已将该照片设为相册封面')
          this.getData()
        } else {
          this.$message.error(res.msg || '设置封面失败')
        }
      })
    },
    handleDeletePhoto(photo, index) {
      this.$confirm('确定要从该相册中删除此照片吗？', '提示', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const jsonData = {
          albumId: this.currentAlbum.albumId,
          postId: photo.id || photo.imageFileId,
          action: 2
        }
        doAlbumAction(jsonData).then(res => {
          if (res.code === 0) {
            this.$message.success('删除成功')
            // 重新请求当前页，保持数据与后端分页对齐
            this.fetchPhotos(this.currentAlbum.albumId)
            if (this.currentAlbum) {
              this.currentAlbum.total = Math.max(0, this.currentAlbum.total - 1)
            }
          } else {
            this.$message.error(res.msg || '删除失败')
          }
        })
      }).catch(() => {})
    },
    handleManageDialogClose() {
      this.showUploadPanel = false
      this.uploadImages = []
      imageFileMap.clear()
    },

    // --- el-upload 回调与校验事件 ---
    handleBeforeUpload(file) {
      let isJPG = false
      if (
        file.type === 'image/jpeg' ||
        file.type === 'image/webp' ||
        file.type === 'image/gif' ||
        file.type === 'image/png'
      ) {
        isJPG = true
      }

      const isLt100M = file.size / 1024 / 1024 < 100
      if (!isJPG) {
        this.$message.error('图片只能是 jpeg/webp/gif/png 格式!')
      }
      if (!isLt100M) {
        this.$message.error('图片大小不能超过 100MB!')
      }
      return isJPG && isLt100M
    },
    handleOnSuccess(res, file) {
      if (res.code === 0) {
        const resData = res.data
        imageFileMap.set(file.name, resData.uploadId)
        this.$message.success('上传成功')

        const jsonData = {
          albumId: this.currentAlbum.albumId,
          postId: resData.uploadId,
          action: 1
        }
        doAlbumAction(jsonData).then(resp => {
          if (resp.code !== 0) {
            this.$message.error(resp.msg)
          }
        }).catch(error => {
          this.$message.error(error.message)
        })

        // 重新拉取相册照片列表，并更新主表总条数
        if (this.currentAlbum) {
          this.currentAlbum.total = (this.currentAlbum.total || 0) + 1
          this.fetchPhotos(this.currentAlbum.albumId)
        }
      } else {
        this.$notify({
          title: '提示',
          message: '图片上传失败，请重试！' + (res.msg || ''),
          type: 'warning',
          duration: 3000
        })
      }
    },
    handleOnError(err) {
      let errMsg = { msg: '网络或服务器错误' }
      try {
        errMsg = JSON.parse(err.message)
      } catch (e) {
        console.error(err)
      }
      this.$notify({
        title: '图片上传失败',
        message: errMsg.msg,
        type: 'error',
        duration: 3000
      })
    },
    handleOnRemove(file) {
      imageFileMap.delete(file.name)
    },
    handleOnPreview(file) {
      this.dialogImageUrl = file.url || file.thumbUrl
      this.previewDialogVisible = true
    }
  }
}
</script>

<style scoped>
.album-manage-container {
  padding: 20px;
  background-color: #fff;
}

.header-action-bar {
  margin-bottom: 16px;
}

.cover-img {
  width: 70px;
  height: 70px;
  border-radius: 4px;
  display: block;
  margin: 0 auto;
  cursor: pointer;
}

.no-cover {
  width: 70px;
  height: 70px;
  background-color: #f5f7fa;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #909399;
  font-size: 12px;
}

.no-cover i {
  font-size: 20px;
  margin-bottom: 2px;
}

.album-link {
  color: #409eff;
  text-decoration: none;
  font-weight: 500;
}

.album-link:hover {
  text-decoration: underline;
}

.pagination-wrapper {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

/* 弹窗工具栏 */
.dialog-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.photo-count-info {
  font-size: 14px;
  color: #606266;
}

.photo-count-info strong {
  color: #409eff;
}

/* 可折叠上传面板 */
.upload-panel {
  background-color: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 16px;
}

/* 照片网格展示区域 */
.photo-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
}

.photo-item {
  position: relative;
  width: 100%;
  height: 130px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
}

.photo-img {
  width: 100%;
  height: 100%;
  display: block;
}

/* 操作蒙层与按钮样式 */
.photo-mask {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.photo-item:hover .photo-mask {
  opacity: 1;
}

.photo-action-btn {
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  transition: transform 0.2s, color 0.2s;
}

.photo-action-btn:hover {
  color: #409eff;
  transform: scale(1.2);
}

.photo-action-btn.delete-btn:hover {
  color: #f56c6c;
}

.empty-photos {
  grid-column: 1 / -1;
}
</style>
